import React, { useState } from 'react';
import { styled } from '@compiled/react';
import { Editor } from '@atlaskit/editor-core';
import Heading from '@atlaskit/heading';
import PageFilledIconNew from '@atlaskit/icon/core/migration/page--page-filled';
import PageFilledIcon from '@atlaskit/icon/glyph/page-filled';
import Lozenge from '@atlaskit/lozenge';
import Popup from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { useJsmPirEntitlementResource } from '@atlassian/jira-router-resources-jsm-pir-entitlement';
import { UpgradePlanContent } from '@atlassian/jira-servicedesk-itsm-features/src/ui/app/sections/service-project-section/practices-features/tile/upgrade-plan-content/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { ExpandButton } from '../../../common/ui/expand-button';
import { pirTemplateAdf } from './constants';
import messages from './messages';

const paddingStyles = xcss({
	marginBottom: 'space.200',
});

const expandableSectionTitleContaineStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
});

export const PostIncidentReviewTemplatesSection = () => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const { cloudId } = useTenantContext();
	const { isEntitled: isPirEntitled } = useJsmPirEntitlementResource(cloudId);
	const [isPaymentPopperOpen, setIsPaymentPopperOpen] = useState(false);

	return (
		<>
			{ff('im_feature_enablement_gating') ? (
				<Heading level="h400">
					<Box xcss={!isPirEntitled && disabledTextStyle}>
						{formatMessage(messages.pirTemplatesSubSectionHead)}
					</Box>
				</Heading>
			) : (
				<Heading level="h400">{formatMessage(messages.pirTemplatesSubSectionHead)}</Heading>
			)}

			<ExpandableSectionContainer>
				<Box xcss={[expandableSectionTitleContaineStyles, isExpanded && paddingStyles]}>
					<LeftSideTitleContainer>
						{ff('im_feature_enablement_gating') ? (
							<PageFilledIconNew
								color={isPirEntitled ? undefined : token('color.icon.disabled', '#BFDBF847')}
								label=""
							/>
						) : (
							<PageFilledIcon label="" />
						)}

						<SectionHeading isDisabled={!isPirEntitled}>
							{formatMessage(messages.pirDefaultTemplateName)}
						</SectionHeading>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, jira/react/no-style-attribute -- Ignored via go/DSP-18766 */}
						<Lozenge style={isPirEntitled ? undefined : disabledLozengeStyle}>
							{formatMessage(messages.pirDefaultTemplatesLozengeText)}
						</Lozenge>
					</LeftSideTitleContainer>

					<Box>
						{ff('im_feature_enablement_gating') ? (
							<Popup
								isOpen={isPaymentPopperOpen}
								onClose={() => setIsPaymentPopperOpen(false)}
								placement="bottom-end"
								content={() => (
									<Box xcss={upgradePopperStyles}>
										<UpgradePlanContent feature="postIncidentReview" />
									</Box>
								)}
								trigger={(triggerProps) => (
									// eslint-disable-next-line react/jsx-props-no-spreading
									<Box {...triggerProps}>
										<ExpandButton
											isDisabled={!isPirEntitled}
											label={formatMessage(messages.pirTemplatesSubSectionHead)}
											onClick={() => {
												setIsPaymentPopperOpen(!isPirEntitled && !isPaymentPopperOpen);
												setIsExpanded(isPirEntitled && !isExpanded);
											}}
											isExpanded={isPirEntitled && isExpanded}
										/>
									</Box>
								)}
							/>
						) : (
							<ExpandButton
								label={formatMessage(messages.pirTemplatesSubSectionHead)}
								onClick={() => setIsExpanded(!isExpanded)}
								isExpanded={isExpanded}
							/>
						)}
					</Box>
				</Box>
				{isExpanded && (
					<Box xcss={editorWrapperStyles}>
						<Editor
							appearance="chromeless"
							defaultValue={pirTemplateAdf}
							disabled
							featureFlags={{
								ufo: ff('issue.details.editor.ufo'),
							}}
						/>
					</Box>
				)}
			</ExpandableSectionContainer>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionHeading = styled.h5<{ isDisabled?: boolean }>({
	margin: `0 ${token('space.150', '12px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	color: ({ isDisabled }) =>
		isDisabled ? token('color.text.disabled') : token('color.text.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExpandableSectionContainer = styled.div({
	margin: `${token('space.200', '16px')} 0`,
	padding: `${token('space.200', '16px')} ${token('space.300', '24px')}`,
	backgroundColor: token('elevation.surface.raised', 'transparent'),
	boxShadow: token(
		'elevation.shadow.raised',
		'0px 1px 1px rgba(9, 30, 66, 0.25), 0px 0px 1px rgba(9, 30, 66, 0.31)',
	),
	borderRadius: '3px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LeftSideTitleContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

const editorWrapperStyles = xcss({
	marginTop: 'space.200',
});

const disabledTextStyle = xcss({
	color: 'color.text.disabled',
});

const disabledLozengeStyle = {
	backgroundColor: token('color.background.disabled', '#091e4289'),
	color: token('color.text.disabled'),
};
const upgradePopperStyles = xcss({ padding: 'space.150', width: '300px' });
